import React, { useState, useEffect } from "react";

import { motion } from "framer-motion";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import { returnFromDetail } from "../utils/navigation";

const getProviderColor = (provider) => {
  if (provider === "amazon") return "text-orange-500";
  if (provider === "apple") return "text-gray-500";

  return "text-blue-500";
};

const formatInvoiceStatus = (status) => {
  const labels = {
    paid: "Paid",
    trial: "Active Trial",
    converted_trial: "Converted Trial",
    expired_trial: "Expired Trial",
    refunded: "Refunded",
  };

  if (labels[status]) return labels[status];

  return String(status || "Unknown")
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatTrialEnd = (trialEnd) => {
  if (!trialEnd) return "";

  const text = String(trialEnd);
  const date = /^\d+$/.test(text)
    ? new Date(
        Number(text) < 1000000000000 ? Number(text) * 1000 : Number(text)
      )
    : new Date(trialEnd);

  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      try {
        const invoiceData = await apiService.getInvoice(id);
        const paymentProvider = invoiceData.paymentProvider || "stripe";
        const paymentProviderLabel =
          invoiceData.paymentProviderLabel || "Stripe";
        const paymentProviderIcon =
          invoiceData.paymentProviderIcon ||
          (paymentProvider === "amazon"
            ? "fab fa-amazon"
            : paymentProvider === "apple"
              ? "fab fa-apple"
              : "fas fa-credit-card");
        const paymentMethodLabel =
          invoiceData.paymentMethodLabel ||
          (invoiceData.paymentMethodBrand
            ? `${invoiceData.paymentMethodBrand} ****${invoiceData.paymentMethodLast4}`
            : "Credit Card");

        // Transform the invoice data to match component expectations
        const formattedInvoice = {
          id: invoiceData.transactionId,
          invoiceNumber:
            invoiceData.prettyInvoiceId || invoiceData.invoiceNumber,
          customer: {
            name: invoiceData.customer,
            email: invoiceData.customerEmail,
            phone: "",
            company: "Lunova Player",
            address: invoiceData.customerAddress || "",
          },
          issueDate: new Date(invoiceData.issueDate).toLocaleDateString(
            "en-CA"
          ), // YYYY-MM-DD format
          status: invoiceData.status,
          currency: "USD",
          subtotal: invoiceData.amount,
          tax: 0,
          total: invoiceData.total,
          paid:
            invoiceData.status === "paid"
              ? invoiceData.total
              : invoiceData.status === "trial"
                ? 0
                : 0,
          balance:
            invoiceData.status === "paid"
              ? 0
              : invoiceData.status === "trial"
                ? invoiceData.total
                : invoiceData.total,
          items: invoiceData.items || [],
          notes: invoiceData.isTrialTransaction
            ? "Thank you for starting your trial of Lunova Player."
            : invoiceData.subscriptionType === "single-use"
              ? "Thank you for your purchase."
              : "Thank you for your subscription to Lunova Player.",
          paymentTerms: invoiceData.isTrialTransaction
            ? "Trial Period"
            : invoiceData.subscriptionType === "single-use"
              ? "One-time Purchase"
              : "Auto-renewal Subscription",
          stripeInvoiceId: invoiceData.stripeInvoiceId,
          stripeCustomerId: invoiceData.stripeCustomerId,
          stripeSubscriptionId: invoiceData.stripeSubscriptionId,
          redeemCodes: invoiceData.redeemCodes || [],
          billingReason: invoiceData.billingReason,
          isRenewal: invoiceData.isRenewal,
          isTrialTransaction: invoiceData.isTrialTransaction,
          trialEnd: invoiceData.trialEnd,
          subscriptionType: invoiceData.subscriptionType,
          paymentMethodLast4: invoiceData.paymentMethodLast4,
          paymentMethodBrand: invoiceData.paymentMethodBrand,
          paymentProvider,
          paymentProviderLabel,
          paymentProviderIcon,
          paymentMethodLabel,
          registrationId: invoiceData.registrationId,
          paymentHistory:
            invoiceData.status === "paid" && invoiceData.amount > 0
              ? [
                  {
                    id: 1,
                    date: new Date(invoiceData.issueDate).toLocaleString(),
                    amount: invoiceData.total,
                    method: paymentMethodLabel,
                    status: "completed",
                  },
                ]
              : [],
          activities: [
            {
              id: 1,
              date: new Date(invoiceData.createdAt).toLocaleString(),
              action:
                invoiceData.billingReason === "subscription_create"
                  ? invoiceData.isTrialTransaction
                    ? "Trial started"
                    : "Subscription created"
                  : invoiceData.isRenewal
                    ? "Subscription renewed"
                    : "Invoice created",
              user: "System",
              icon: "fas fa-plus-circle",
              color: "text-green-500",
            },
            ...(invoiceData.status === "paid" && invoiceData.amount > 0
              ? [
                  {
                    id: 2,
                    date: new Date(invoiceData.createdAt).toLocaleString(),
                    action: "Payment processed",
                    user: paymentProviderLabel,
                    icon: paymentProviderIcon,
                    color: getProviderColor(paymentProvider),
                  },
                ]
              : []),
          ],
        };

        setInvoice(formattedInvoice);
      } catch (error) {
        console.error("Error fetching invoice:", error);
        toast.error("Failed to load invoice details");
        navigate("/invoices");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [id, navigate]);

  const handleStatusChange = (newStatus) => {
    setInvoice({ ...invoice, status: newStatus });
    toast.success(`Invoice marked as ${newStatus}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    toast.success("Downloading invoice PDF...");
  };

  const handleBack = () => {
    returnFromDetail({
      fallback: "/invoices",
      navigate,
      state: location.state,
    });
  };

  const handleDuplicate = () => {
    toast.success("Invoice duplicated successfully");
    navigate("/invoices");
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      toast.success("Invoice deleted");
      navigate("/invoices");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "badge-success";
      case "trial":
        return "badge-info";
      case "converted_trial":
        return "badge-primary";
      case "expired_trial":
        return "badge-secondary";
      case "refunded":
        return "badge-danger";
      case "pending":
        return "badge-warning";
      case "overdue":
        return "badge-danger";
      case "draft":
        return "badge-info";
      case "cancelled":
        return "badge-secondary";
      default:
        return "badge-secondary";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="fas fa-spinner fa-spin text-3xl text-gray-400" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            aria-label="Back to previous page"
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            onClick={handleBack}
          >
            <i className="fas fa-arrow-left" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Invoice #{invoice.invoiceNumber}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Created on {new Date(invoice.issueDate).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`badge ${getStatusColor(invoice.status)}`}>
            {formatInvoiceStatus(invoice.status)}
          </span>
          <div className="flex gap-2">
            <button
              className="btn btn-ghost"
              title="Print"
              onClick={handlePrint}
            >
              <i className="fas fa-print" />
              <span className="hidden md:inline ml-2">Print</span>
            </button>
            <button
              className="btn btn-ghost"
              title="Download PDF"
              onClick={handleDownload}
            >
              <i className="fas fa-download" />
              <span className="hidden md:inline ml-2">Download</span>
            </button>
            {user?.role === "admin" && (
              <button
                className="btn btn-primary"
                onClick={() => setShowEmailModal(true)}
              >
                <i className="fas fa-paper-plane" />
                <span className="hidden md:inline ml-2">Send Invoice</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Details - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Document */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="card p-8 print:shadow-none shadow-md"
            initial={{ opacity: 0, y: 20 }}
          >
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center text-white text-2xl font-bold mb-4">
                  BT
                </div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  INVOICE
                </h2>
                <p className="text-gray-500 dark:text-gray-400">
                  #{invoice.invoiceNumber}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Issue Date
                </p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(invoice.issueDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Billing Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  From
                </h3>
                <div className="text-gray-900 dark:text-white">
                  <p className="font-semibold">Lunova App, LLC</p>
                  <p className="mt-2">support@lunova.app</p>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Bill To
                </h3>
                <div className="text-gray-900 dark:text-white">
                  <p className="font-semibold">{invoice.customer.name}</p>
                  <p>{invoice.customer.company}</p>
                  <div className="whitespace-pre-line">
                    {invoice.customer.address}
                  </div>
                  <p className="mt-2">{invoice.customer.email}</p>
                  <p>{invoice.customer.phone}</p>
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                      Description
                    </th>
                    <th className="text-center py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                      Qty
                    </th>
                    <th className="text-right py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                      Rate
                    </th>
                    <th className="text-right py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item) => (
                    <tr
                      className="border-b border-gray-100 dark:border-gray-800"
                      key={item.id}
                    >
                      <td className="py-4 text-gray-900 dark:text-white">
                        {item.description}
                      </td>
                      <td className="py-4 text-center text-gray-900 dark:text-white">
                        {item.quantity}
                      </td>
                      <td className="py-4 text-right text-gray-900 dark:text-white">
                        ${item.rate.toFixed(2)}
                      </td>
                      <td className="py-4 text-right font-medium text-gray-900 dark:text-white">
                        ${item.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64">
                <div className="flex justify-between py-2">
                  <span className="text-gray-500 dark:text-gray-400">
                    Subtotal
                  </span>
                  <span className="text-gray-900 dark:text-white">
                    ${invoice.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-500 dark:text-gray-400">
                    Tax (10%)
                  </span>
                  <span className="text-gray-900 dark:text-white">
                    ${invoice.tax.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    Total
                  </span>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    ${invoice.total.toFixed(2)}
                  </span>
                </div>
                {invoice.paid > 0 && (
                  <>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500 dark:text-gray-400">
                        Paid
                      </span>
                      <span className="text-green-600 dark:text-green-400">
                        -${invoice.paid.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between py-3 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        Balance Due
                      </span>
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        ${invoice.balance.toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Notes
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {invoice.notes}
                </p>
              </div>
            )}

            {/* Payment Terms */}
            <div className="mt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Payment Terms:{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {invoice.paymentTerms}
                </span>
              </p>
              {invoice.isTrialTransaction && invoice.trialEnd && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Trial Ends:{" "}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {formatTrialEnd(invoice.trialEnd)}
                  </span>
                </p>
              )}
            </div>
          </motion.div>

          {/* Activity Timeline */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="card p-6 shadow-md"
            initial={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Activity
            </h3>
            <div className="space-y-4">
              {invoice.activities.map((activity) => (
                <div className="flex items-start gap-3" key={activity.id}>
                  <div
                    className={`w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center ${activity.color}`}
                  >
                    <i className={`${activity.icon} text-sm`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 dark:text-white">
                      {activity.action}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {activity.date} by {activity.user}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Quick Actions - Admin Only */}
          {user?.role === "admin" && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 shadow-md"
              initial={{ opacity: 0, y: 20 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Quick Actions
              </h3>
              <div className="space-y-2">
                {invoice.status === "pending" && (
                  <button
                    className="btn btn-primary w-full"
                    onClick={() => setShowPaymentModal(true)}
                  >
                    <i className="fas fa-credit-card mr-2" />
                    Record Payment
                  </button>
                )}
                <button
                  className="btn btn-ghost w-full"
                  onClick={() => handleStatusChange("paid")}
                >
                  <i className="fas fa-check mr-2" />
                  Mark as Paid
                </button>
                <button
                  className="btn btn-ghost w-full"
                  onClick={() => setShowEmailModal(true)}
                >
                  <i className="fas fa-envelope mr-2" />
                  Send Reminder
                </button>
                <button
                  className="btn btn-ghost w-full"
                  onClick={handleDuplicate}
                >
                  <i className="fas fa-copy mr-2" />
                  Duplicate Invoice
                </button>
                <button
                  className="btn btn-ghost w-full"
                  onClick={() => navigate(`/invoices/${id}/edit`)}
                >
                  <i className="fas fa-edit mr-2" />
                  Edit Invoice
                </button>
                <button
                  className="btn btn-ghost w-full text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={handleDelete}
                >
                  <i className="fas fa-trash mr-2" />
                  Delete Invoice
                </button>
              </div>
            </motion.div>
          )}

          {/* Payment Method */}
          {(invoice.paymentMethodLast4 ||
            invoice.paymentProvider !== "stripe") && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Payment Method
              </h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-8 rounded flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                  <i
                    className={
                      invoice.paymentProvider === "stripe" &&
                      invoice.paymentMethodBrand
                        ? `fab fa-cc-${invoice.paymentMethodBrand.toLowerCase()} text-gray-600 dark:text-gray-400 text-lg`
                        : `${invoice.paymentProviderIcon} text-gray-600 dark:text-gray-400 text-lg`
                    }
                  />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {invoice.paymentMethodLast4
                      ? `${(invoice.paymentMethodBrand || "Card").charAt(0).toUpperCase()}${(invoice.paymentMethodBrand || "card").slice(1)} •••• ${invoice.paymentMethodLast4}`
                      : invoice.paymentMethodLabel}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Processed via {invoice.paymentProviderLabel}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Payment History */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="card p-6 shadow-md"
            initial={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Payment History
            </h3>
            {invoice.paymentHistory.length > 0 ? (
              <div className="space-y-3">
                {invoice.paymentHistory.map((payment) => (
                  <div
                    className="border-l-4 border-green-500 pl-4"
                    key={payment.id}
                  >
                    <p className="font-medium text-gray-900 dark:text-white">
                      ${payment.amount.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {payment.date} via {payment.method}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                No payments recorded yet
              </p>
            )}
          </motion.div>

          {/* Customer Info - Admin Only */}
          {user?.role === "admin" && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-200"
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.3 }}
              onClick={() => navigate(`/customers/${invoice.registrationId}`)}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Customer
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Name
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {invoice.customer.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Company
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {invoice.customer.company}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Email
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {invoice.customer.email}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                    <i className="fas fa-external-link-alt mr-2" />
                    Click to view customer profile
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          onClick={() => setShowPaymentModal(false)}
        >
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md"
            exit={{ scale: 0.9, opacity: 0 }}
            initial={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Record Payment
            </h2>
            <form className="space-y-4">
              <div>
                <label className="label">Payment Amount</label>
                <input
                  className="input"
                  defaultValue={invoice.balance.toFixed(2)}
                  placeholder="0.00"
                  type="number"
                />
              </div>
              <div>
                <label className="label">Payment Date</label>
                <input
                  className="input"
                  defaultValue={new Date().toISOString().split("T")[0]}
                  type="date"
                />
              </div>
              <div>
                <label className="label">Payment Method</label>
                <select className="input">
                  <option>Credit Card</option>
                  <option>Bank Transfer</option>
                  <option>PayPal</option>
                  <option>Check</option>
                  <option>Cash</option>
                </select>
              </div>
              <div>
                <label className="label">Transaction ID (Optional)</label>
                <input className="input" placeholder="TXN-123456" type="text" />
              </div>
              <div>
                <label className="label">Notes (Optional)</label>
                <textarea
                  className="input"
                  placeholder="Payment notes..."
                  rows="3"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  className="btn btn-ghost flex-1"
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary flex-1"
                  type="submit"
                  onClick={(e) => {
                    e.preventDefault();
                    handleStatusChange("paid");
                    setShowPaymentModal(false);
                    toast.success("Payment recorded successfully");
                  }}
                >
                  Record Payment
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          onClick={() => setShowEmailModal(false)}
        >
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg"
            exit={{ scale: 0.9, opacity: 0 }}
            initial={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Send Invoice
            </h2>
            <form className="space-y-4">
              <div>
                <label className="label">To</label>
                <input
                  className="input"
                  defaultValue={invoice.customer.email}
                  type="email"
                />
              </div>
              <div>
                <label className="label">CC (Optional)</label>
                <input
                  className="input"
                  placeholder="cc@example.com"
                  type="email"
                />
              </div>
              <div>
                <label className="label">Subject</label>
                <input
                  className="input"
                  defaultValue={`Invoice ${invoice.invoiceNumber} from Lunova`}
                  type="text"
                />
              </div>
              <div>
                <label className="label">Message</label>
                <textarea
                  className="input"
                  defaultValue={`Dear ${invoice.customer.name},\n\nPlease find attached invoice ${invoice.invoiceNumber} for ${invoice.currency} ${invoice.total.toFixed(2)}.\n\nThank you for your business!\n\nBest regards,\nLunova Support Team`}
                  rows="6"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  defaultChecked
                  className="rounded"
                  id="attach-pdf"
                  type="checkbox"
                />
                <label
                  className="text-sm text-gray-700 dark:text-gray-300"
                  htmlFor="attach-pdf"
                >
                  Attach invoice as PDF
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  className="btn btn-ghost flex-1"
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary flex-1"
                  type="submit"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowEmailModal(false);
                    toast.success("Invoice sent successfully");
                  }}
                >
                  Send Invoice
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default InvoiceDetail;
